import { makeAutoObservable, runInAction } from 'mobx';
import { firebaseService } from 'src/services/firebaseService';
import { QuestionObj } from '../types/quiz'; // Same QuestionObj as in QuizStore

/**
 * This store handles all local-only scratchpad logic.
 * It is not synced to Firebase or any other remote storage.
 */
class ScratchpadStore {
  scratchpad: QuestionObj[] = [];

  constructor() {
    makeAutoObservable(this);
    this.loadState();
  }

  get scratchpadQuestions() {
    return this.scratchpad.slice().reverse();
  }

  addQuestionToScratchpad(question: string, answer: string): QuestionObj {
    const newQuestion = new QuestionObj({
      id: firebaseService.generateId(),
      question,
      answer,
      approved: false,
    });
    runInAction(() => {
      this.scratchpad.push(newQuestion);
      this.saveState();
      // As in your original code, keep the scratchpad size to ~400
      if (this.scratchpad.length > 400) {
        const toRemove = this.scratchpad.slice(200);
        this.removeQuestions(new Set(toRemove.map((q) => q.id)));
      }
    });
    return newQuestion;
  }

  removeQuestions(ids: Set<string>) {
    runInAction(() => {
      this.scratchpad = this.scratchpad.filter((q) => !ids.has(q.id));
      this.saveState();
    });
  }

  approveQuestion(id: string) {
    // This just sets "approved" in local scratchpad,
    // actual "move" into the main store is handled in QuizStore’s approve logic if desired.
    const index = this.scratchpad.findIndex((q) => q.id === id);
    if (index !== -1) {
      runInAction(() => {
        this.scratchpad[index].approved = true;
        this.saveState();
      });
    }
  }

  unapproveQuestion(id: string) {
    const question = this.scratchpad.find((q) => q.id === id);
    if (question) {
      runInAction(() => {
        question.approved = false;
        this.saveState();
      });
    }
  }

  saveState() {
    localStorage.setItem('scratchpad', JSON.stringify(this.scratchpad));
  }

  loadState() {
    const savedScratchpad = localStorage.getItem('scratchpad');

    if (savedScratchpad) {
      // Convert plain objects back into QuestionObj instances
      const parsed = JSON.parse(savedScratchpad);
      this.scratchpad = parsed.map((obj: any) => new QuestionObj(obj));
    }
  }
}

export const scratchpadStore = new ScratchpadStore();
